import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {buildAccessibleButtonProps} from '../../../libs/shared/helpers/src/accessibility/accessibilityHelpers';
import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

interface SettingsNavSwitcherStyleProps {
  $selectedFilterIndex: number;
}

interface SettingsNavSwitcherProps {
  onCompanyClick: Handler;
  onPersonalClick: Handler;
  selectedFilterIndex: number;
  isGlobalAdmin: boolean;
}

/*
 * Style.
 */

const StyledSwitcherDiv = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;

  background-color: ${(p) => p.theme.alphas.gray10};
  border-radius: 100px;
`;

// Common transition for the animated elements (easeOutQuint).
const transition = '0.3s cubic-bezier(0.230,1.000,0.320,1.000)';

const StyledFiltersSelectedDiv = styled.div<SettingsNavSwitcherStyleProps>`
  grid-column: 1;
  grid-row: 1;
  pointer-events: none;

  position: relative;
  overflow: hidden;
  border-radius: 100px;

  transform: translateX(${(p) => p.$selectedFilterIndex * 100}%);
  transition: transform ${transition};
`;

const StyledFiltersSelectedContentDiv = styled.div<SettingsNavSwitcherStyleProps>`
  position: absolute;
  width: 200%;

  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;

  transform: translateX(-${(p) => p.$selectedFilterIndex * (100 / 2)}%);
  transition: transform ${transition};

  color: ${(p) => p.theme.greys.shade30};
  background-color: ${(p) => p.theme.greys.black};
`;

interface GridItemProps {
  $column?: number;
  $row?: number;
}

const StyledFilterGridItem = styled.div<GridItemProps>`
  min-width: 0;
  min-height: 0;
  grid-column: ${(p) => p.$column || 1};
  grid-row: ${(p) => p.$row || 1};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
`;

const StyledLabelDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  line-height: 14px;
  font-weight: ${fontWeights.medium};
  ${ellipsis()};

  ${StyledSwitcherDiv}:hover & {
    color: ${(p) => p.theme.greys.shade80};
  }
`;

const StyledSelectedLabelDiv = styled(StyledLabelDiv)`
  color: ${(p) => p.theme.greys.white};
  font-weight: ${fontWeights.semibold};

  ${StyledSwitcherDiv}:hover & {
    color: ${(p) => p.theme.greys.white};
  }
`;

const StyledSwitcherWrapperDiv = styled.div`
  padding: 0 20px;
  margin-top: 17px;
`;

/*
 * Component.
 */

export const SettingsNavSwitcher: FC<SettingsNavSwitcherProps> = (props) => (
  <StyledSwitcherWrapperDiv>
    <StyledSwitcherDiv>
      <StyledFilterGridItem $column={1} {...buildAccessibleButtonProps({onClick: props.onCompanyClick})}>
        <StyledLabelDiv>
          {props.isGlobalAdmin ? (
            <Trans id="settings-switcher-company">Company</Trans>
          ) : (
            <Trans id="settings-switcher-team">Team</Trans>
          )}
        </StyledLabelDiv>
      </StyledFilterGridItem>
      <StyledFilterGridItem $column={2} {...buildAccessibleButtonProps({onClick: props.onPersonalClick})}>
        <StyledLabelDiv>
          <Trans id="settings-switcher-personal">Personal</Trans>
        </StyledLabelDiv>
      </StyledFilterGridItem>
      <StyledFiltersSelectedDiv $selectedFilterIndex={props.selectedFilterIndex}>
        <StyledFiltersSelectedContentDiv $selectedFilterIndex={props.selectedFilterIndex}>
          <StyledFilterGridItem $column={1}>
            <StyledSelectedLabelDiv>
              {props.isGlobalAdmin ? (
                <Trans id="settings-switcher-company">Company</Trans>
              ) : (
                <Trans id="settings-switcher-team">Team</Trans>
              )}
            </StyledSelectedLabelDiv>
          </StyledFilterGridItem>
          <StyledFilterGridItem $column={2}>
            <StyledSelectedLabelDiv>
              <Trans id="settings-switcher-personal">Personal</Trans>
            </StyledSelectedLabelDiv>
          </StyledFilterGridItem>
        </StyledFiltersSelectedContentDiv>
      </StyledFiltersSelectedDiv>
    </StyledSwitcherDiv>
  </StyledSwitcherWrapperDiv>
);
